// Client time uses the mailbox calendar zone, which may differ from the browser.
// These functions convert between wall-clock dates and real instants.

import {
  ClientTimeConverter,
  WallTimeFields,
} from '@maximizer/outlook/shared/domain';

function clientTimeConverter(): ClientTimeConverter | null {
  const mailbox = globalThis.Office?.context?.mailbox as
    Partial<ClientTimeConverter> | undefined;
  return typeof mailbox?.convertToLocalClientTime === 'function' &&
    typeof mailbox.convertToUtcClientTime === 'function'
    ? (mailbox as ClientTimeConverter)
    : null;
}

export function toClientWallTime(instant: Date): Date {
  const converter = clientTimeConverter();
  if (!converter) return instant;

  const local = converter.convertToLocalClientTime(instant);
  return new Date(
    local.year,
    local.month,
    local.date,
    local.hours,
    local.minutes,
    local.seconds,
    local.milliseconds,
  );
}

function wallTimeFields(wallTime: Date): WallTimeFields {
  return {
    year: wallTime.getFullYear(),
    month: wallTime.getMonth(),
    date: wallTime.getDate(),
    hours: wallTime.getHours(),
    minutes: wallTime.getMinutes(),
    seconds: wallTime.getSeconds(),
    milliseconds: wallTime.getMilliseconds(),
  };
}

function mailboxOffsetAt(
  converter: ClientTimeConverter,
  instant: Date,
): number {
  return converter.convertToLocalClientTime(instant).timezoneOffset;
}

// The wall-clock Date is not the true instant, so its offset can sit on the wrong side of a
// DST transition; the offset is re-read at the instant the first pass produced. A wall time
// inside the repeated fall-back hour is ambiguous and resolves to its first occurrence.
export function toUtcInstant(wallTime: Date): Date {
  const converter = clientTimeConverter();
  if (!converter) return wallTime;

  const fields = wallTimeFields(wallTime);
  const guessedOffset = mailboxOffsetAt(converter, wallTime);
  const firstPass = converter.convertToUtcClientTime({
    ...fields,
    timezoneOffset: guessedOffset,
  });
  const actualOffset = mailboxOffsetAt(converter, firstPass);

  return actualOffset === guessedOffset
    ? firstPass
    : converter.convertToUtcClientTime({
        ...fields,
        timezoneOffset: actualOffset,
      });
}

export function clientNow(): Date {
  return toClientWallTime(new Date());
}
